import {TextModel} from "../../models/Text"
import {ColourwayData, SummaryType} from "../../models/productSummaryApi"
import {DEPARTMENT_MENSWEAR, DEPARTMENT_HOMEWARE, SuitType, SofaType, BundleType} from "../../config/constants"

export const formatTooltipTitle = (
    title: string,
    colourwayItemNumber: string,
    price: string,
    salePrice?: string | null,
) => `${title} (${colourwayItemNumber}) | ${salePrice ?? price}`

export const formatSofaTooltipTitle = (
    title: string,
    colourwayItemNumber: string,
    price: string,
    salePrice?: string | null,
) => {
    const displayId = colourwayItemNumber.slice(colourwayItemNumber.length - 6)
    return formatTooltipTitle(title, displayId, price, salePrice)
}

export const formatPdpLink = (baseUrl: string, colourwayUrl: string) => `${baseUrl}/${colourwayUrl}`.toLowerCase()

export interface FormatTitleOptions {
    title: string
    department: string
    selectedColourway: ColourwayData | undefined
    fit?: string | null
    isEnglishLang: boolean
}

export const formatTitle = ({
    title,
    department = "",
    fit = "",
    selectedColourway,
    isEnglishLang,
}: FormatTitleOptions): string => {
    const lowercaseDept = department?.toLowerCase()
    let fitValue
    let colourValue
    const titleValue = title || ""

    if (!isEnglishLang) {
        fitValue = fit && lowercaseDept === DEPARTMENT_MENSWEAR ? `${fit} - ` : ""
        colourValue = (selectedColourway?.c && `${selectedColourway?.c} - `) || ""
    } else {
        fitValue = fit && lowercaseDept === DEPARTMENT_MENSWEAR ? `${fit} ` : ""
        colourValue = (selectedColourway?.c && `${selectedColourway?.c} `) || ""
    }

    if (lowercaseDept !== DEPARTMENT_HOMEWARE) {
        return `${colourValue}${fitValue}${titleValue}`
    }

    return titleValue
}

// Suit Functions
interface FormatSuitTitleParams {
    defaultTitle: string
    title?: string
    selectedColourway: ColourwayData | undefined
    fit?: string | null
    isEnglishLang: boolean
}

export const formatSuitTitle = ({
    title,
    defaultTitle,
    fit,
    selectedColourway,
    isEnglishLang,
}: FormatSuitTitleParams): string => {
    let fitValue = ""
    let colourValue = ""

    const titleValue = defaultTitle.split(":")[0] || ""

    if (!isEnglishLang) {
        fitValue = fit ? `${fit} - ` : ""
        colourValue = (selectedColourway?.c && `${selectedColourway?.c} - `) || ""
    } else {
        if (title) return title.split(":")[0]
        return defaultTitle.split(":")[0]
    }
    return `${colourValue}${fitValue}${titleValue}`
}

interface FormatSuitTooltipTitleParams extends FormatSuitTitleParams {
    colourwayItemNumber: string
    price: string
    salePrice?: string | null
}

export const formatSuitTooltipTitle = ({
    title,
    defaultTitle,
    colourwayItemNumber,
    price,
    salePrice,
    fit,
    selectedColourway,
    isEnglishLang,
}: FormatSuitTooltipTitleParams): string => {
    return formatTooltipTitle(
        formatSuitTitle({title, defaultTitle, fit, selectedColourway, isEnglishLang}),
        colourwayItemNumber,
        price,
        salePrice,
    )
}

// Bundle Functions
interface FormatBundleTitleParams {
    defaultTitle: string
    title?: string
    selectedColourway: ColourwayData | undefined
    fit?: string | null
    isEnglishLang: boolean
}

export const formatBundleTitle = ({
    title,
    defaultTitle,
    fit,
    selectedColourway,
    isEnglishLang,
}: FormatBundleTitleParams): string => {
    let fitValue = ""
    let colourValue = ""

    const titleValue = defaultTitle.split(":")[0] || ""

    if (!isEnglishLang) {
        fitValue = fit ? `${fit} - ` : ""
        colourValue = (selectedColourway?.c && `${selectedColourway?.c} - `) || ""
    } else {
        if (title) return title.split(":")[0]
        return defaultTitle.split(":")[0]
    }
    return `${colourValue}${fitValue}${titleValue}`
}

interface FormatBundleTooltipTitleParams extends FormatBundleTitleParams {
    colourwayItemNumber: string
    price: string
    salePrice?: string | null
}

export const formatBundleTooltipTitle = ({
    title,
    defaultTitle,
    colourwayItemNumber,
    price,
    salePrice,
    fit,
    selectedColourway,
    isEnglishLang,
}: FormatBundleTooltipTitleParams): string => {
    return formatTooltipTitle(
        formatBundleTitle({title, defaultTitle, fit, selectedColourway, isEnglishLang}),
        colourwayItemNumber,
        price,
        salePrice,
    )
}

export function getToolTipTitleByType(
    type: SummaryType,
    {title, defaultTitle, colourwayItemNumber, price, salePrice, fit, selectedColourway, isEnglishLang},
) {
    switch (type) {
        case SuitType:
            return formatSuitTooltipTitle({
                title,
                defaultTitle,
                colourwayItemNumber,
                price,
                salePrice,
                fit,
                selectedColourway,
                isEnglishLang,
            })
        case BundleType:
            return formatBundleTooltipTitle({
                title,
                defaultTitle,
                colourwayItemNumber,
                price,
                salePrice,
                fit,
                selectedColourway,
                isEnglishLang,
            })
        case SofaType:
            return formatSofaTooltipTitle(defaultTitle, colourwayItemNumber, price, salePrice)
        default:
            return formatTooltipTitle(defaultTitle, colourwayItemNumber, price, salePrice)
    }
}

interface FormatFavouritesLabelarams {
    isEnglishLang: boolean
    text: TextModel
    selectedColourwayTitle: string | undefined
    defaultTitle: string
    selectedColourway: ColourwayData | undefined
    fit?: string | null
    isFav: boolean
}
export const formatFavouritesTitleAndLabel = ({
    isEnglishLang,
    text,
    selectedColourway,
    selectedColourwayTitle,
    fit,
    defaultTitle,
    isFav,
}: FormatFavouritesLabelarams) => {
    if (isEnglishLang)
        return `${isFav ? text.product.removeFavourites : text.product.favourites} ${selectedColourwayTitle}`

    const fitValue = fit ? `${fit} - ` : ""
    const colourValue = (selectedColourway?.c && `${selectedColourway?.c} - `) || ""

    return `${
        isFav ? text.product.removeFavourites : text.product.favourites
    } - ${colourValue}${fitValue}${defaultTitle} -`
}

interface ProductPriceAriaLabelTextParams {
    title: string
    price: string
    isEnglishLang: boolean
    wasPriceText: string
    salePriceText: string
    salePrice?: string | null
    wasPrice?: string | null
}

export const productPriceAriaLabelText = ({
    title,
    price,
    salePrice,
    wasPrice,
    wasPriceText,
    salePriceText,
    isEnglishLang,
}: ProductPriceAriaLabelTextParams) => {
    if (!salePrice) {
        if (isEnglishLang) {
            return `${title} ${price}`
        }
        return `${title} - ${price}`
    }

    if (isEnglishLang) {
        return `${title} ${wasPriceText} ${wasPrice} ${salePriceText} ${salePrice}`
    }
    return `${title} - ${wasPriceText} ${wasPrice} - ${salePriceText} ${salePrice}`
}

// Suit Labels
interface SuitPriceComponentAriaLabelTextParams {
    title: string
    wasPriceText: string
    salePriceText: string
    isEnglishLang: boolean
    price: string
    wasPrice?: string
    salePrice?: string
    jacketsHasSale: boolean
    trousersHasSale: boolean
    priceText: "Jacket" | "Trousers" | "Price"
}

export const suitPriceComponentAriaLabelText = ({
    title,
    wasPriceText,
    salePriceText,
    price,
    wasPrice,
    salePrice,
    isEnglishLang,
    jacketsHasSale,
    trousersHasSale,
    priceText,
}: SuitPriceComponentAriaLabelTextParams) => {
    const suitsHasSale = jacketsHasSale || trousersHasSale
    const wasSuitPriceText = `${wasPriceText} ${wasPrice}`
    const saleSuitPriceText = `${salePriceText} ${salePrice}`

    if (!suitsHasSale) {
        if (isEnglishLang) {
            return `${title} ${priceText} ${price}`
        }
        return `${title} - ${priceText} - ${price}`
    }

    if (isEnglishLang) {
        return `${title} ${priceText} ${wasSuitPriceText} ${saleSuitPriceText}`
    }
    return `${title} - ${priceText} - ${wasSuitPriceText} - ${saleSuitPriceText}`
}

// Bundle Labels

interface BundlePriceComponentAriaLabelTextParams {
    title: string
    wasPriceText: string
    salePriceText: string
    isEnglishLang: boolean
    price: string
    wasPrice?: string
    salePrice?: string
    basesHasSale: boolean
    shadesHasSale: boolean
    priceText: "Base Price" | "Shades Price" | "Price"
}

export const bundlePriceComponentAriaLabelText = ({
    title,
    wasPriceText,
    salePriceText,
    price,
    wasPrice,
    salePrice,
    isEnglishLang,
    basesHasSale,
    shadesHasSale,
    priceText,
}: BundlePriceComponentAriaLabelTextParams) => {
    const bundleHasSale = basesHasSale || shadesHasSale
    const wasBundlePriceText = `${wasPriceText} ${wasPrice}`
    const saleBundlePriceText = `${salePriceText} ${salePrice}`

    if (!bundleHasSale) {
        if (isEnglishLang) {
            return `${title} ${priceText} ${price}`
        }
        return `${title} - ${priceText} - ${price}`
    }

    if (isEnglishLang) {
        return `${title} ${priceText} ${wasBundlePriceText} ${saleBundlePriceText}`
    }
    return `${title} - ${priceText} - ${wasBundlePriceText} - ${saleBundlePriceText}`
}
